import { useState, useEffect, useMemo, useCallback, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getJson, getJsonUnwrapped, http, unwrapPaginatedResponse } from "@/lib/http";
import API_BASE from "@/lib/apiBase";
import { useLocation } from "wouter";
import { Plus, Smartphone, Edit, Trash2, Building2, Upload, Download, FileText, AlertCircle, CheckCircle2, X, Calendar, ArrowLeft, Eye, DollarSign, Package, ChevronDown, ChevronUp, ChevronRight, AlertTriangle, Settings, Lock, Unlock, Loader2, BarChart3, Archive, ArchiveRestore } from "lucide-react";
import { DataTable } from "@/components/tables/data-table";
import { createBookingCycleColumns } from "@/components/tables/booking-cycle-columns";
import { createPackColumns } from "@/components/tables/pack-columns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PackEditorDialog } from "@/components/packs/PackEditorDialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PackStatusBadge } from "@/components/packs/PackStatusBadge";
import type { Retailer, StoreFormat, Store as StoreType, InsertRetailer, InsertStoreFormat, InsertStore, MediaFormat, InsertMediaFormat, StoreMediaRequirement, InsertStoreMediaRequirement, User, BookingCycle, InsertBookingCycle, Pack, InsertPack, CycleRateTemplate, InsertCycleRateTemplate, RateTemplate, InsertRateTemplate, StoreFormatOperatorAssignment, Task, PackTaskAssignment, FormatGroupItem, AcceptableFormatGroups } from "@shared/schema";
import { usePackStatus } from "@/hooks/usePackStatus";
import { YearFilter } from "@/components/filters/YearFilter";
import { useYear } from "@/state/yearStore";

export type RetailerWithFormats = Retailer & {
  formats: StoreFormat[];
  storeCount: number;
};

export interface BookingCyclesListProps {
  retailerId: number;
  retailer?: RetailerWithFormats;
}

export function BookingCyclesList({ retailerId, retailer }: BookingCyclesListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const year = useYear();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [cycleToDelete, setCycleToDelete] = useState<BookingCycle | null>(null);
  const [editingCycle, setEditingCycle] = useState<BookingCycle | null>(null);
  const [cycleFormData, setCycleFormData] = useState<Partial<InsertBookingCycle>>({
    name: "",
    startDate: new Date(),
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    bookingDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
    retailerId: retailerId,
  });

  const { data: allCycles = [], isLoading } = useQuery<BookingCycle[]>({
    queryKey: [`/api/booking-cycles?retailerId=${retailerId}`],
    queryFn: async () => {
      console.log(`[BOOKING_CYCLES_DEBUG] Fetching cycles for retailerId=${retailerId}`);
      const res = await apiFetch(`/api/booking-cycles?retailerId=${retailerId}`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const data = await res.json();
      console.log(`[BOOKING_CYCLES_DEBUG] Received ${data.length} cycles for retailerId=${retailerId}:`, data.map((c: any) => `${c.id}(r${c.retailerId})`));
      return data.sort((a: BookingCycle, b: BookingCycle) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );
    },
  });

  const cycles = useMemo(() => {
    return allCycles.filter((c) => {
      const startYear = c.startDate ? new Date(c.startDate).getFullYear() : null;
      const endYear = c.endDate ? new Date(c.endDate).getFullYear() : null;
      return startYear === year || endYear === year;
    });
  }, [allCycles, year]);

  const createCycleMutation = useMutation({
    mutationFn: async (data: InsertBookingCycle) => {
      return apiRequest("/api/booking-cycles", "POST", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/booking-cycles?retailerId=${retailerId}`] });
      setIsCreateDialogOpen(false);
      setCycleFormData({
        name: "",
        startDate: new Date(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        bookingDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        retailerId: retailerId
      });
      toast({ title: "Success", description: "Booking cycle created successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create booking cycle", variant: "destructive" });
    },
  });

  const updateCycleMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<BookingCycle> }) => {
      return apiRequest(`/api/booking-cycles/${data.id}`, "PATCH", data.updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/booking-cycles?retailerId=${retailerId}`] });
      setIsEditDialogOpen(false);
      setEditingCycle(null);
      setCycleFormData({
        name: "",
        startDate: new Date(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        bookingDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        retailerId: retailerId
      });
      toast({ title: "Success", description: "Booking cycle updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update booking cycle", variant: "destructive" });
    },
  });

  const deleteCycleMutation = useMutation({
    mutationFn: (cycleId: number) => apiRequest(`/api/booking-cycles/${cycleId}`, "DELETE"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/booking-cycles?retailerId=${retailerId}`], refetchType: 'active' });
      await queryClient.refetchQueries({ queryKey: [`/api/booking-cycles?retailerId=${retailerId}`] });
      setIsDeleteDialogOpen(false);
      setCycleToDelete(null);
      toast({ title: "Success", description: "Booking cycle deleted successfully" });
    },
    onError: (error: any) => {
      const message = error.message || "Failed to delete booking cycle";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const handleCreateCycle = () => {
    if (!cycleFormData.name) {
      toast({ title: "Error", description: "Cycle name is required", variant: "destructive" });
      return;
    }

    // Ensure dates are properly formatted
    const cycleData: InsertBookingCycle = {
      name: cycleFormData.name,
      startDate: cycleFormData.startDate instanceof Date ? cycleFormData.startDate : new Date(cycleFormData.startDate!),
      endDate: cycleFormData.endDate instanceof Date ? cycleFormData.endDate : new Date(cycleFormData.endDate!),
      bookingDeadline: cycleFormData.bookingDeadline instanceof Date ? cycleFormData.bookingDeadline : new Date(cycleFormData.bookingDeadline!),
      retailerId: retailerId
    };

    createCycleMutation.mutate(cycleData);
  };

  const handleEditCycle = (cycle: BookingCycle) => {
    setEditingCycle(cycle);
    setCycleFormData({
      name: cycle.name,
      startDate: new Date(cycle.startDate),
      endDate: new Date(cycle.endDate),
      bookingDeadline: new Date(cycle.bookingDeadline),
      retailerId: cycle.retailerId,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateCycle = () => {
    if (!cycleFormData.name || !editingCycle) {
      toast({ title: "Error", description: "Cycle name is required", variant: "destructive" });
      return;
    }

    const cycleData = {
      name: cycleFormData.name,
      startDate: cycleFormData.startDate instanceof Date ? cycleFormData.startDate : new Date(cycleFormData.startDate!),
      endDate: cycleFormData.endDate instanceof Date ? cycleFormData.endDate : new Date(cycleFormData.endDate!),
      bookingDeadline: cycleFormData.bookingDeadline instanceof Date ? cycleFormData.bookingDeadline : new Date(cycleFormData.bookingDeadline!),
    };

    updateCycleMutation.mutate({
      id: editingCycle.id,
      updates: cycleData,
    });
  };

  const handleDeleteCycle = (cycle: BookingCycle) => {
    setCycleToDelete(cycle);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteCycle = () => {
    if (cycleToDelete) {
      deleteCycleMutation.mutate(cycleToDelete.id);
    }
  };

  const formatDate = useCallback((date: Date | string) => {
    return new Date(date).toLocaleDateString();
  }, []);

  const columns = useMemo(
    () => createBookingCycleColumns({
      onEdit: handleEditCycle,
      onDelete: handleDeleteCycle,
      formatDate,
    }),
    [formatDate]
  );

  const formatDateForInput = (date: Date | string | null | undefined) => {
    if (!date) return '';

    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if the date is valid
    if (isNaN(dateObj.getTime())) return '';

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Mutation for updating retailer calendar color
  const updateColorMutation = useMutation({
    mutationFn: async ({ id, color }: { id: number; color: string }) => {
      console.log('[COLOR_UPDATE] Attempting to update color for retailer', id, 'to', color);
      const response = await apiFetch(`/api/retailers/${id}/color`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ color }),
      });
      console.log('[COLOR_UPDATE] Response status:', response.status, response.statusText);
      if (!response.ok) {
        let errorMessage = 'Failed to update color';
        try {
          const error = await response.json();
          errorMessage = error.error || error.message || errorMessage;
          console.error('[COLOR_UPDATE] Error response:', error);
        } catch (e) {
          const text = await response.text().catch(() => '');
          errorMessage = `Failed to update color (${response.status}): ${text || response.statusText}`;
          console.error('[COLOR_UPDATE] Error text:', text);
        }
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onMutate: async ({ id, color }) => {
      await queryClient.cancelQueries({ queryKey: ['/api/retailers'] });
      const previousRetailers = queryClient.getQueryData<RetailerWithFormats[]>(['/api/retailers']);
      queryClient.setQueryData<RetailerWithFormats[]>(['/api/retailers'], (old) =>
        old?.map((r) => (r.id === id ? { ...r, calendarColor: color, calendar_color: color } : r)) || []
      );
      return { previousRetailers };
    },
    onError: (err, variables, context) => {
      if (context?.previousRetailers) {
        queryClient.setQueryData(['/api/retailers'], context.previousRetailers);
      }
      toast({
        title: 'Failed to update color',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Color updated',
        description: 'Retailer calendar color has been updated successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/retailers'] });
    },
  });

  const handleColorChange = async (id: number, color: string) => {
    updateColorMutation.mutate({ id, color });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold">Booking Cycles</h3>
          <p className="text-sm text-muted-foreground">Manage media booking cycles for this retailer</p>
        </div>
        <div className="flex items-center gap-3">
          <YearFilter showIcon={false} />
          {retailer && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-200 bg-gray-50">
              <Label htmlFor={`color-${retailer.id}`} className="text-xs text-muted-foreground cursor-pointer">
                Calendar Color:
              </Label>
              <input
                id={`color-${retailer.id}`}
                type="color"
                value={(retailer as any).calendarColor || (retailer as any).calendar_color || '#95A5A6'}
                onChange={(e) => handleColorChange(retailer.id, e.target.value)}
                className="w-7 h-7 rounded border cursor-pointer"
                disabled={updateColorMutation.isPending}
                title="Set calendar color for this retailer"
              />
            </div>
          )}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Cycle
              </Button>
            </DialogTrigger>
            <DialogContent className={cn("bg-white", "max-w-2xl")}>
              <DialogHeader>
                <DialogTitle>Create Booking Cycle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cycleName">Cycle Name</Label>
                  <Input
                    id="cycleName"
                    value={cycleFormData.name}
                    onChange={(e) => setCycleFormData({ ...cycleFormData, name: e.target.value })}
                    placeholder="e.g., Q1 2025, Holiday Campaign"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={formatDateForInput(cycleFormData.startDate) || formatDateForInput(new Date())}
                      onChange={(e) => {
                        const dateValue = e.target.value ? new Date(e.target.value + 'T00:00:00') : new Date();
                        setCycleFormData({ ...cycleFormData, startDate: dateValue });
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={formatDateForInput(cycleFormData.endDate) || formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))}
                      onChange={(e) => {
                        const dateValue = e.target.value ? new Date(e.target.value + 'T00:00:00') : new Date();
                        setCycleFormData({ ...cycleFormData, endDate: dateValue });
                      }}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="bookingDeadline">Booking Deadline</Label>
                  <Input
                    id="bookingDeadline"
                    type="date"
                    value={formatDateForInput(cycleFormData.bookingDeadline) || formatDateForInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))}
                    onChange={(e) => {
                      const dateValue = e.target.value ? new Date(e.target.value + 'T00:00:00') : new Date();
                      setCycleFormData({ ...cycleFormData, bookingDeadline: dateValue });
                    }}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateCycle} disabled={createCycleMutation.isPending}>
                    {createCycleMutation.isPending ? "Creating..." : "Create Cycle"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Booking Cycle Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className={cn("bg-white", "max-w-2xl")}>
          <DialogHeader>
            <DialogTitle>Edit Booking Cycle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editCycleName">Cycle Name</Label>
              <Input
                id="editCycleName"
                value={cycleFormData.name}
                onChange={(e) => setCycleFormData({ ...cycleFormData, name: e.target.value })}
                placeholder="e.g., Q1 2025, Holiday Campaign"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editStartDate">Start Date</Label>
                <Input
                  id="editStartDate"
                  type="date"
                  value={formatDateForInput(cycleFormData.startDate) || formatDateForInput(new Date())}
                  onChange={(e) => {
                    const dateValue = e.target.value ? new Date(e.target.value + 'T00:00:00') : new Date();
                    setCycleFormData({ ...cycleFormData, startDate: dateValue });
                  }}
                />
              </div>
              <div>
                <Label htmlFor="editEndDate">End Date</Label>
                <Input
                  id="editEndDate"
                  type="date"
                  value={formatDateForInput(cycleFormData.endDate) || formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))}
                  onChange={(e) => {
                    const dateValue = e.target.value ? new Date(e.target.value + 'T00:00:00') : new Date();
                    setCycleFormData({ ...cycleFormData, endDate: dateValue });
                  }}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="editBookingDeadline">Booking Deadline</Label>
              <Input
                id="editBookingDeadline"
                type="date"
                value={formatDateForInput(cycleFormData.bookingDeadline) || formatDateForInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))}
                onChange={(e) => {
                  const dateValue = e.target.value ? new Date(e.target.value + 'T00:00:00') : new Date();
                  setCycleFormData({ ...cycleFormData, bookingDeadline: dateValue });
                }}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateCycle} disabled={updateCycleMutation.isPending}>
                {updateCycleMutation.isPending ? "Updating..." : "Update Cycle"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {cycles.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No booking cycles configured for this retailer</p>
          <p className="text-sm">Create booking cycles to manage media campaigns</p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={cycles}
          searchKey="name"
          searchPlaceholder="Search by cycle name..."
          showColumnToggle={true}
          isLoading={isLoading}
        />
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className={cn("bg-white")}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking Cycle</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the "{cycleToDelete?.name}" booking cycle?
              This action cannot be undone and will remove all associated campaigns and bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCycle}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCycleMutation.isPending}
            >
              {deleteCycleMutation.isPending ? "Deleting..." : "Delete Cycle"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


interface CycleRateTemplatesListProps {
  retailerId: number;
}

function CycleRateTemplatesList({ retailerId }: CycleRateTemplatesListProps) {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<CycleRateTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<CycleRateTemplate | null>(null);
  const [templateFormData, setTemplateFormData] = useState<Partial<InsertCycleRateTemplate>>({
    retailerId: retailerId,
    cycleId: undefined,
    rateTemplateId: undefined,
  });

  // Fetch cycle rate templates for this retailer
  const { data: cycleRateTemplates = [], isLoading } = useQuery<CycleRateTemplate[]>({
    queryKey: ["/api/cycle-rate-templates"],
    select: (allTemplates: CycleRateTemplate[]) => allTemplates.filter(template => template.retailerId === retailerId),
  });

  // Fetch booking cycles for this retailer
  const { data: cycles = [] } = useQuery<BookingCycle[]>({
    queryKey: [`/api/booking-cycles?retailerId=${retailerId}`],
    queryFn: async () => {
      console.log(`[CYCLE_RATE_TEMPLATES_DEBUG] Fetching cycles for retailerId=${retailerId}`);
      const res = await apiFetch(`/api/booking-cycles?retailerId=${retailerId}`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const data = await res.json();
      console.log(`[CYCLE_RATE_TEMPLATES_DEBUG] Received ${data.length} cycles for retailerId=${retailerId}`);
      return data;
    },
  });

  // Fetch rate templates for this retailer
  const { data: rateTemplates = [] } = useQuery<RateTemplate[]>({
    queryKey: ["/api/rate-templates"],
    select: (allTemplates: RateTemplate[]) => allTemplates.filter(template => template.retailerId === retailerId),
  });



  const createTemplateMutation = useMutation({
    mutationFn: async (data: InsertCycleRateTemplate) => {
      return apiRequest("/api/cycle-rate-templates", "POST", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cycle-rate-templates"] });
      setIsCreateDialogOpen(false);
      setTemplateFormData({
        retailerId: retailerId,
        cycleId: undefined,
        rateTemplateId: undefined,
      });
      toast({ title: "Success", description: "Rate template assignment created successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create rate template assignment", variant: "destructive" });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<CycleRateTemplate> }) => {
      return apiRequest(`/api/cycle-rate-templates/${data.id}`, "PATCH", data.updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cycle-rate-templates"] });
      setIsEditDialogOpen(false);
      setEditingTemplate(null);
      setTemplateFormData({
        retailerId: retailerId,
        cycleId: undefined,
        rateTemplateId: undefined,
      });
      toast({ title: "Success", description: "Rate template assignment updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update rate template assignment", variant: "destructive" });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest(`/api/cycle-rate-templates/${id}`, "DELETE");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/cycle-rate-templates"], refetchType: 'active' });
      await queryClient.refetchQueries({ queryKey: ["/api/cycle-rate-templates"] });
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
      toast({ title: "Success", description: "Rate template assignment deleted successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete rate template assignment", variant: "destructive" });
    },
  });

  const handleCreateTemplate = () => {
    if (!templateFormData.cycleId || !templateFormData.rateTemplateId) {
      toast({ title: "Error", description: "Please select both cycle and rate template", variant: "destructive" });
      return;
    }
    createTemplateMutation.mutate({
      retailerId: retailerId,
      cycleId: templateFormData.cycleId,
      rateTemplateId: templateFormData.rateTemplateId,
    });
  };

  const handleEditTemplate = (template: CycleRateTemplate) => {
    setEditingTemplate(template);
    setTemplateFormData({
      retailerId: template.retailerId,
      cycleId: template.cycleId,
      rateTemplateId: template.rateTemplateId,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateTemplate = () => {
    if (!editingTemplate || !templateFormData.cycleId || !templateFormData.rateTemplateId) {
      toast({ title: "Error", description: "Please select both cycle and rate template", variant: "destructive" });
      return;
    }
    updateTemplateMutation.mutate({
      id: editingTemplate.id,
      updates: {
        retailerId: retailerId,
        cycleId: templateFormData.cycleId,
        rateTemplateId: templateFormData.rateTemplateId,
      },
    });
  };

  const handleDeleteTemplate = (template: CycleRateTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteTemplate = () => {
    if (templateToDelete) {
      deleteTemplateMutation.mutate(templateToDelete.id);
    }
  };

  const getCycleName = (cycleId: number) => {
    const cycle = cycles.find(c => c.id === cycleId);
    return cycle?.name || `Cycle ${cycleId}`;
  };

  const getRateTemplateName = (rateTemplateId: number) => {
    const template = rateTemplates.find(t => t.id === rateTemplateId);
    return template?.name || `Template ${rateTemplateId}`;
  };



  if (isLoading) {
    return <div className="text-center py-4">Loading fee templates...</div>;
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Cycle Operator Fees</h3>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Assign Rate Template
            </Button>
          </DialogTrigger>
          <DialogContent className={cn("bg-white")}>
            <DialogHeader>
              <DialogTitle>Assign Rate Template to Cycle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="cycle">Booking Cycle</Label>
                <Select
                  value={templateFormData.cycleId?.toString() || ""}
                  onValueChange={(value) => setTemplateFormData({ ...templateFormData, cycleId: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select booking cycle" />
                  </SelectTrigger>
                  <SelectContent>
                    {cycles.map((cycle) => (
                      <SelectItem key={cycle.id} value={cycle.id.toString()}>
                        {cycle.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rateTemplate">Rate Template</Label>
                <Select
                  value={templateFormData.rateTemplateId?.toString() || ""}
                  onValueChange={(value) => setTemplateFormData({ ...templateFormData, rateTemplateId: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select rate template" />
                  </SelectTrigger>
                  <SelectContent>
                    {rateTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id.toString()}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleCreateTemplate} disabled={createTemplateMutation.isPending}>
                {createTemplateMutation.isPending ? "Creating..." : "Create Assignment"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Template Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className={cn("bg-white")}>
          <DialogHeader>
            <DialogTitle>Edit Rate Template Assignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editCycle">Booking Cycle</Label>
              <Select
                value={templateFormData.cycleId?.toString() || ""}
                onValueChange={(value) => setTemplateFormData({ ...templateFormData, cycleId: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select booking cycle" />
                </SelectTrigger>
                <SelectContent>
                  {cycles.map((cycle) => (
                    <SelectItem key={cycle.id} value={cycle.id.toString()}>
                      {cycle.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="editRateTemplate">Rate Template</Label>
              <Select
                value={templateFormData.rateTemplateId?.toString() || ""}
                onValueChange={(value) => setTemplateFormData({ ...templateFormData, rateTemplateId: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select rate template" />
                </SelectTrigger>
                <SelectContent>
                  {rateTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id.toString()}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateTemplate} disabled={updateTemplateMutation.isPending}>
                {updateTemplateMutation.isPending ? "Updating..." : "Update Assignment"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {cycleRateTemplates.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No rate template assignments configured for this retailer</p>
          <p className="text-sm">Assign rate templates to booking cycles to automate commission calculations</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Booking Cycle</TableHead>
              <TableHead>Rate Template</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cycleRateTemplates.map((template) => (
              <TableRow key={template.id}>
                <TableCell className="font-medium">{getCycleName(template.cycleId)}</TableCell>
                <TableCell>{getRateTemplateName(template.rateTemplateId)}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditTemplate(template)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTemplate(template)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className={cn("bg-white")}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Fee Template Assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this fee template assignment?
              This will affect commission calculations for campaigns in this cycle.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTemplate}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTemplateMutation.isPending}
            >
              {deleteTemplateMutation.isPending ? "Deleting..." : "Delete Assignment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
